import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowRight, Users, Building2, Landmark, FlaskConical, Target, TrendingUp, Cpu, Lightbulb, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BackNavigation from "@/components/BackNavigation";

// Cinematic visual assets - 4K UHD AI/Futuristic Theme (Dark cinematic tone)
const CINEMATIC_VISUALS = {
  heroVideo: "https://videos.pexels.com/video-files/5384607/5384607-uhd_2560_1440_24fps.mp4",
  heroPoster: "https://images.pexels.com/photos/27141314/pexels-photo-27141314.jpeg?w=1920&q=80",
  lab: "https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?w=1920"
};

/**
 * Zodiac Lab Landing Page
 * The application engine of WAIT Theory
 * Contains ONLY 3 labs: Workforce Lab, Organization Lab, Institutional Lab
 */
export default function ZodiacLab() {
  const labs = [
    {
      id: "workforce",
      icon: Users,
      title: "Workforce Lab",
      tagline: "Individual Intelligence Development",
      description: "Develop personal workforce intelligence through assessment, profile development, skill alignment, and opportunity discovery.",
      features: [
        "Personal Assessment & Profile Intelligence",
        "Development Pathway Planning",
        "AI Tool Alignment & Training",
        "Opportunity Intelligence Engine",
        "Career & Service Pathways"
      ],
      color: "#1E3A8A",
      link: "/zodiac-lab/workforce",
      cta: "Enter Workforce Lab"
    },
    {
      id: "organization",
      icon: Building2,
      title: "Organization Lab",
      tagline: "Organizational Intelligence Systems",
      description: "Diagnose, align, and optimize workforce intelligence at the organizational level with AI integration support.",
      features: [
        "Workforce Diagnostics & Analytics",
        "Alignment & Performance Analysis",
        "AI Integration Strategy",
        "Team Development Programs",
        "Organizational Benchmarking"
      ],
      color: "#2563EB",
      link: "/zodiac-lab/organization",
      cta: "Enter Organization Lab"
    },
    {
      id: "institutional",
      icon: Landmark,
      title: "Institutional Lab",
      tagline: "Sector & System Intelligence",
      description: "Analyze and optimize workforce intelligence at the sector, industry, and policy level.",
      features: [
        "Sector-Level Intelligence",
        "Workforce System Analysis",
        "Policy & Framework Support",
        "Industry Benchmarking",
        "Regional Workforce Intelligence"
      ],
      color: "#3B82F6",
      link: "/zodiac-lab/institutional",
      cta: "Enter Institutional Lab"
    }
  ];

  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.6;
      const handleLoaded = () => setVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="zodiac-lab-page">
      {/* Back Navigation */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section - Cinematic Video Background */}
      <section className="relative overflow-hidden min-h-[70vh] flex items-center -mt-12">
        {/* Video Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
              opacity: videoLoaded ? 0 : 1
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={CINEMATIC_VISUALS.heroPoster}
          >
            <source src={CINEMATIC_VISUALS.heroVideo} type="video/mp4" />
          </video>
          {/* Gradient Overlay - 50% opacity for dark cinematic futuristic feel */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/55 via-[#1E293B]/50 to-[#0F172A]/55" />
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6">
              <FlaskConical className="w-4 h-4" />
              Application Engine
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
              Zodiac Lab
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-white/90 mb-6">
              Where WAIT Theory Becomes Applied Intelligence
            </p>
            <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              Zodiac Lab operationalizes WAIT Theory across three levels: individual workforce development, 
              organizational capability building, and institutional intelligence systems. Choose your entry point 
              based on your role and objectives.
            </p>
            <Link to="/wait-theory">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1E3A8A]">
                <Lightbulb className="w-4 h-4 mr-2" />
                Learn About WAIT Theory
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Three Labs Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Three Labs. Three Levels of Application.
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Each lab applies WAIT Theory at a specific level of workforce intelligence development.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {labs.map((lab, index) => (
              <motion.div
                key={lab.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="h-full"
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="h-2" style={{ backgroundColor: lab.color }}></div>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${lab.color}15` }}>
                      <lab.icon className="w-8 h-8" style={{ color: lab.color }} />
                    </div>
                    
                    <p className="text-sm font-medium mb-2" style={{ color: lab.color }}>{lab.tagline}</p>
                    <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: '#991B1B' }}>
                      {lab.title}
                    </h3>
                    <p className="text-[#1E3A8A]/80 mb-6">{lab.description}</p>
                    
                    <div className="space-y-3 mb-8">
                      {lab.features.map((feature, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: lab.color }}></div>
                          <p className="text-sm text-[#1E3A8A]/70">{feature}</p>
                        </div>
                      ))}
                    </div>

                    <Link to={lab.link}>
                      <Button 
                        className="w-full text-white group-hover:shadow-lg transition-shadow"
                        style={{ backgroundColor: lab.color }}
                      >
                        {lab.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Tool Architecture */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
                AI-Integrated Learning
              </h2>
              <p className="text-lg text-[#1E3A8A] mb-6">
                Every lab integrates AI tools from the Zodiac AI Capability Registry, managed by the AI Observatory.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#E5E7EB]">
                  <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                    <Cpu className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#991B1B]">Zodiac AI Capability Registry</h4>
                    <p className="text-sm text-[#1E3A8A]/70">Central system for all AI tool identification and standardization</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#E5E7EB]">
                  <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#991B1B]">Lab AI Tool Libraries</h4>
                    <p className="text-sm text-[#1E3A8A]/70">Curated AI tools displayed within each lab context</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#E5E7EB]">
                  <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#991B1B]">AI Observatory Management</h4>
                    <p className="text-sm text-[#1E3A8A]/70">Evaluation, standardization, and integration support</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="border-[#E5E7EB] bg-white">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#991B1B]/10 flex items-center justify-center mx-auto mb-4">
                      <Rocket className="w-8 h-8 text-[#991B1B]" />
                    </div>
                    <h3 className="font-heading text-xl font-bold" style={{ color: '#991B1B' }}>
                      Your Lab Journey
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">1</div>
                      <p className="text-[#1E3A8A]">Select your lab based on your role</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">2</div>
                      <p className="text-[#1E3A8A]">Complete diagnostic assessment</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">3</div>
                      <p className="text-[#1E3A8A]">Receive your intelligence profile</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">4</div>
                      <p className="text-[#1E3A8A]">Follow your development pathway</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#991B1B] text-white flex items-center justify-center text-sm font-bold">5</div>
                      <p className="text-[#1E3A8A]">Discover opportunities</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 overflow-hidden">
        {/* Background texture layer */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${CINEMATIC_VISUALS.lab})`,
            opacity: 0.15
          }}
        />
        {/* Color overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/95 to-[#1E40AF]/95" />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to Begin?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Start with Workforce Lab for individual development, or explore Organization and Institutional Labs for system-level intelligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/zodiac-lab/workforce">
              <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-14 px-8">
                Start with Workforce Lab
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
